import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { PartnerApi } from '@eis/partnerservice-sdk';
import { useApiClients } from './ApiClientsProvider';
import { apiKeys } from './keys';
import { isApiError, unwrap } from './result';
import { useServiceMutation, type ServiceMutation } from './useServiceMutation';

/**
 * The PARTNER layer (SDD-014): the Partner domain's records through the Partner domain's API.
 *
 * Another team owns these shapes — `PartnerApi.*` is their published document, vendored verbatim
 * (PS-11) — and this file adds nothing to them: no derived name, no second id, no type the
 * service does not have. What the console needs beyond the SDK is only what every layer needs —
 * cache keys, invalidation and the conflict discipline — and those come from the same helpers the
 * insurance layer uses.
 *
 * What is DIFFERENT from the insurance layer, and why:
 *  - No ETag. The service emits none and ignores If-Match, so `useServiceMutation`'s conflict
 *    branch never fires here; a duplicate `partnerNumber` is a plain 409 the caller reads with
 *    `isPartnerNumberTaken`.
 *  - Identity is the service's `code` (`pr_…`), minted on create — a caller never chooses it
 *    (PS-1, O-5). The one identity field a caller may supply is `partnerNumber`.
 *  - The type slug on the wire is the service's spelling (`organization`); the insurance layer's
 *    `partnerTypes` use the platform's (`organisation`) and the insuranceservice translates at
 *    ITS boundary (STOP-3). Screens that show both must not compare them raw.
 */

/**
 * The ERN of a partner (decision T-25, ern-grammar.md): the Partner service's code, never a
 * number and never a name. The wire's own `ern` field carries the tenant slug and is ignored (PS-2).
 */
export const partnerErn = (code: string): string => `partnerservice:partner/${code}`;

/**
 * STOP-3: the Partner service spells the organisation type `organization`; the platform's role
 * rules (`partnerTypes`) and the manifest schema spell it `organisation`. The insuranceservice
 * translates at its boundary (adapters/partnerpmm/slug.go); the console does the same at ITS
 * boundary — a manifest stores the platform's spelling, a create request sends the service's.
 */
// SSOT SDD-014 STOP-3 deferred
export const platformPartnerTypeSlug = (slug: string): string => (slug === 'organization' ? 'organisation' : slug);
export const servicePartnerTypeSlug = (slug: string): string => (slug === 'organisation' ? 'organization' : slug);

export type Partner = PartnerApi.components['schemas']['PartnerClass'];
export type PartnerType = PartnerApi.components['schemas']['PartnerTypeClass'];
export type PartnerTag = PartnerApi.components['schemas']['TagClass'];
export type ListPartnersResponse = PartnerApi.components['schemas']['ListPartnersResponseClass'];
export type ListPartnerVersionsResponse =
  PartnerApi.components['schemas']['ListPartnerVersionsResponseClass'];
export type ListPartnerTypesResponse = PartnerApi.components['schemas']['ListPartnerTypesResponseClass'];
export type ListPartnerTagsResponse = PartnerApi.components['schemas']['ListTagsResponseClass'];
export type GetPartnerResponse = PartnerApi.components['schemas']['GetPartnerResponseClass'];
export type CreatePartnerResponse = PartnerApi.components['schemas']['CreatePartnerResponseClass'];
export type UpdatePartnerResponse = PartnerApi.components['schemas']['UpdatePartnerResponseClass'];

const V1 = '/partnerservice/v1';

/**
 * What a search sends, in the two spellings the service was OBSERVED to honour (PROBE.md §6,
 * 2026-09-20): `search=` matches the partner's data text and its number (`Schmidt`, `Makler`,
 * `PRT-leasingbank` all hit); `filters=partnerNumber==<n>` is exact. The `filters` grammar has
 * more operators, but `code=(a|b)`, `tagSlugs=@x` and `partnerTypeCodes=(x)` were observed NOT
 * to filter, so nothing here offers them — a filter that returns every row is worse than none.
 */
export interface PartnerSearch {
  /** Free text over the partner's data and number. */
  readonly term?: string;
  /** Exact match on the caller-supplied number — the seed's correlation key (PS-14). */
  readonly partnerNumber?: string;
  readonly pageSize?: number;
  /** The service's offset token (`nextPageToken: "2"`, then `""`). */
  readonly pageToken?: string;
}

/** Only what is set — an empty `search=` is a different request than no search. */
export function partnerSearchQuery(search: PartnerSearch): Record<string, string> {
  const out: Record<string, string> = {};
  const term = search.term?.trim() ?? '';
  if (term !== '') out.search = term;
  const number = search.partnerNumber?.trim() ?? '';
  if (number !== '') out.filters = `partnerNumber==${number}`;
  if (search.pageSize !== undefined) out.pageSize = String(search.pageSize);
  if (search.pageToken !== undefined && search.pageToken !== '') out.pageToken = search.pageToken;
  return out;
}

/**
 * The overview's and the picker's read: one page of partners, filtered SERVER-side. `enabled`
 * lets a picker wait for a term without an extra `if` in every screen.
 */
export function usePartnersSearch(
  search: PartnerSearch = {},
  options?: { readonly enabled?: boolean },
): UseQueryResult<ListPartnersResponse> {
  const { partners } = useApiClients();
  const query = partnerSearchQuery(search);
  return useQuery({
    queryKey: apiKeys.partners.search(query),
    queryFn: () => unwrap(partners.GET(`${V1}/partners`, { params: { query } })),
    enabled: options?.enabled ?? true,
  });
}

/** One partner by the code the service minted. 404 surfaces as data (`isNotFound`), like a policy. */
export function usePartner(
  code: string,
  options?: { readonly enabled?: boolean },
): UseQueryResult<GetPartnerResponse> {
  const { partners } = useApiClients();
  return useQuery({
    queryKey: apiKeys.partners.partner(code),
    queryFn: () => unwrap(partners.GET(`${V1}/partners/{code}`, { params: { path: { code } } })),
    enabled: (options?.enabled ?? true) && code !== '',
  });
}

/**
 * Every version the service kept of one partner — what a policy's `partnerVersion` pin points
 * into (PS-7): "the bank as it looked when this segment was approved" is a row here.
 */
export function usePartnerVersions(
  code: string,
  options?: { readonly enabled?: boolean },
): UseQueryResult<ListPartnerVersionsResponse> {
  const { partners } = useApiClients();
  return useQuery({
    queryKey: apiKeys.partners.versions(code),
    queryFn: () =>
      unwrap(partners.GET(`${V1}/partners/{code}/versions`, { params: { path: { code } } })),
    enabled: (options?.enabled ?? true) && code !== '',
  });
}

/**
 * The tenant's partner TYPES as the Partner service holds them — the catalog a role's
 * `partnerTypes` are checked against at publish (PS-9) and a create dialog offers. `Pmm` in the
 * name keeps it apart from the insurance layer's `partnerTypes` constant (the offline default).
 */
export function usePmmPartnerTypes(): UseQueryResult<ListPartnerTypesResponse> {
  const { partners } = useApiClients();
  return useQuery({
    queryKey: apiKeys.partners.types(),
    queryFn: () =>
      unwrap(partners.GET(`${V1}/partner-types`, { params: { query: { pageSize: 100 } } })),
    // A catalog: it changes when somebody authors a type, not between two keystrokes.
    staleTime: 5 * 60 * 1000,
  });
}

/** The tag catalog — the roles (SDD-011 P-3) as the Partner service spells them, with labels. */
export function usePmmTags(): UseQueryResult<ListPartnerTagsResponse> {
  const { partners } = useApiClients();
  return useQuery({
    queryKey: apiKeys.partners.tags(),
    queryFn: () => unwrap(partners.GET(`${V1}/tags`, { params: { query: { pageSize: 100 } } })),
    staleTime: 5 * 60 * 1000,
  });
}

export interface CreatePartnerVariables {
  /** The TYPE's code (`pr_t_…`), from `usePmmPartnerTypes` — never a slug. */
  readonly partnerTypeCode: string;
  /** The type's field groups, keyed by group name (`BasicInformation`, `ContactInformation`, …). */
  readonly customFields: Record<string, unknown>;
  /** Optional caller-supplied number; the same number twice is a 409 (`isPartnerNumberTaken`). */
  readonly partnerNumber?: string;
}

/**
 * Creates a partner; the service mints the code and derives the display name. Invalidates every
 * search — the new row belongs in any of them — and nothing else: no policy names it yet.
 */
export function useCreatePartner(): ServiceMutation<CreatePartnerVariables, CreatePartnerResponse> {
  const { partners } = useApiClients();
  return useServiceMutation({
    mutationFn: (variables) =>
      unwrap(
        partners.POST(`${V1}/partners`, {
          body: {
            partnerTypeCode: variables.partnerTypeCode,
            customFields: variables.customFields,
            ...(variables.partnerNumber === undefined || variables.partnerNumber === ''
              ? {}
              : { partnerNumber: variables.partnerNumber }),
          },
        }),
      ),
    invalidates: () => [apiKeys.partners.list()],
  });
}

export interface UpdatePartnerVariables {
  readonly code: string;
  /** The whole `customFields` document — the service REPLACES it (PROBE.md §6). */
  readonly customFields: Record<string, unknown>;
}

/**
 * Replaces a partner's data; the service writes a new version. The partner, its versions and
 * every search re-read; policies that pin the previous version are untouched by design (PS-7).
 */
export function useUpdatePartner(): ServiceMutation<UpdatePartnerVariables, UpdatePartnerResponse> {
  const { partners } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ code, customFields }) =>
      unwrap(
        partners.PUT(`${V1}/partners/{code}`, {
          params: { path: { code } },
          body: { customFields },
        }),
      ),
    invalidates: ({ code }) => [
      apiKeys.partners.list(),
      apiKeys.partners.partner(code),
      apiKeys.partners.versions(code),
    ],
  });
}

export interface TagPartnerVariables {
  readonly code: string;
  /** The COMPLETE set — the PATCH replaces, it does not add (PROBE.md §5). */
  readonly slugs: readonly string[];
}

/**
 * Sets a partner's tags. A tag is a role the partner MAY hold (SDD-011 P-3); linking a partner into
 * a role on a policy does not write it (P-11), so this is the explicit act. Also a version.
 */
export function useTagPartner(): ServiceMutation<TagPartnerVariables, GetPartnerResponse> {
  const { partners } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ code, slugs }) =>
      unwrap(
        partners.PATCH(`${V1}/partners/{code}/tag`, {
          params: { path: { code } },
          body: { slug: [...slugs] },
        }),
      ),
    invalidates: ({ code }) => [
      apiKeys.partners.list(),
      apiKeys.partners.partner(code),
      apiKeys.partners.versions(code),
    ],
  });
}

export interface DeletePartnerVariables {
  readonly code: string;
}

/**
 * Removes a partner — a SOFT delete as observed (PROBE.md §6: the read answers 404 afterwards, the
 * versions stay readable). Nothing else on the platform deletes a partner; the console offers it to
 * the admin alone (SDD-014 §6.4, §8). The searches and the partner's own reads re-read.
 */
export function useDeletePartner(): ServiceMutation<DeletePartnerVariables, PartnerApi.components['schemas']['DeleteResponseClass']> {
  const { partners } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ code }) =>
      unwrap(partners.DELETE(`${V1}/partners/{code}`, { params: { path: { code } } })),
    invalidates: ({ code }) => [
      apiKeys.partners.list(),
      apiKeys.partners.partner(code),
      apiKeys.partners.versions(code),
    ],
  });
}

/**
 * The Partner service's refusal of a second partner with the same number: a bare 409 with no
 * ErrorInfo (PROBE.md §6, observed 2026-09-20) — which is why the SDK carries no ETag middleware
 * that would have read it as "the data changed, reload".
 */
export function isPartnerNumberTaken(error: unknown): boolean {
  return isApiError(error) && error.status === 409;
}

/**
 * The Partner service unreachable or refusing to serve: the console shows the outage as an
 * outage — an id without a name, an action disabled with the reason — never as "no partners"
 * (SDD-014 §7, `partners:outage`).
 */
export function isPartnerServiceUnavailable(error: unknown): boolean {
  return isApiError(error) && (error.status === 502 || error.status === 503 || error.status === 504);
}

// ---------------------------------------------------------------------------------------------
// The type's AUTHORING schema (PS-8): `partnerType.fields.items` is the list of field GROUPS the
// type was created with — `BasicInformation`, `ContactInformation`, … — each with its properties.
// The console renders forms and read-only views FROM it, so a type this build has never seen
// still gets its fields; `partnerType.schema` (the Joi descriptor) is the service's own business.
// ---------------------------------------------------------------------------------------------

export interface PartnerFieldSpec {
  readonly name: string;
  /** The authoring type: `string`, `date`, `email`, `phone`, `number`, `boolean`, `array`, `object`, … */
  readonly type: string;
  readonly label: string;
  readonly isRequired: boolean;
  /** For `array`/`object`: the nested fields (an array's ONE item node's properties). */
  readonly properties: readonly PartnerFieldSpec[];
}

export interface PartnerFieldGroup {
  readonly name: string;
  readonly label: string;
  readonly isRequired: boolean;
  readonly fields: readonly PartnerFieldSpec[];
}

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;

function fieldSpecOf(node: unknown): PartnerFieldSpec | undefined {
  const record = asRecord(node);
  if (record === undefined || typeof record.name !== 'string') return undefined;
  const type = typeof record.type === 'string' ? record.type : 'string';
  // An array's `items` is ONE named node (PROBE.md §6); its properties are the item's fields.
  const nested: unknown = type === 'array' ? asRecord(record.items)?.properties : record.properties;
  const properties = Array.isArray(nested)
    ? nested.map(fieldSpecOf).filter((field): field is PartnerFieldSpec => field !== undefined)
    : [];
  return {
    name: record.name,
    type,
    label: typeof record.label === 'string' && record.label !== '' ? record.label : record.name,
    isRequired: record.isRequired === true,
    properties,
  };
}

/** The type's field groups, in authoring order; empty when the type carries none this reader understands. */
export function partnerTypeFieldGroups(type: { readonly fields?: Record<string, unknown> } | undefined): PartnerFieldGroup[] {
  const items: unknown = type?.fields?.items;
  if (!Array.isArray(items)) return [];
  const groups: PartnerFieldGroup[] = [];
  for (const node of items) {
    const spec = fieldSpecOf(node);
    if (spec === undefined) continue;
    groups.push({ name: spec.name, label: spec.label, isRequired: spec.isRequired, fields: spec.properties });
  }
  return groups;
}

/**
 * A form's values, FLAT: `BasicInformation.firstName`, `ContactInformation.address.0.street`. One
 * key per input, so refusals and required markers address inputs and nothing has to be walked.
 */
export type PartnerFieldValues = Readonly<Record<string, string>>;

const scalarText = (value: unknown): string =>
  typeof value === 'string' ? value : typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';

/** The inputs a group renders, as `(key, spec)` pairs — an array field contributes its FIRST item's fields. */
export function partnerFieldInputs(group: PartnerFieldGroup): readonly { readonly key: string; readonly spec: PartnerFieldSpec; readonly label: string }[] {
  const out: { key: string; spec: PartnerFieldSpec; label: string }[] = [];
  for (const field of group.fields) {
    if (field.type === 'array' || field.type === 'object') {
      const prefix = field.type === 'array' ? `${group.name}.${field.name}.0` : `${group.name}.${field.name}`;
      for (const sub of field.properties) {
        out.push({ key: `${prefix}.${sub.name}`, spec: sub, label: `${field.label} · ${sub.label}` });
      }
      continue;
    }
    out.push({ key: `${group.name}.${field.name}`, spec: field, label: field.label });
  }
  return out;
}

/** An existing partner's `customFields`, flattened onto the form's keys (an edit starts from what is stored). */
export function partnerFieldValuesOf(groups: readonly PartnerFieldGroup[], customFields: Record<string, unknown> | undefined): PartnerFieldValues {
  const out: Record<string, string> = {};
  for (const group of groups) {
    const groupValue = asRecord(customFields?.[group.name]) ?? {};
    for (const { key, spec } of partnerFieldInputs(group)) {
      const path = key.split('.').slice(1); // drop the group
      let cursor: unknown = groupValue;
      for (const segment of path) {
        if (Array.isArray(cursor)) cursor = cursor[Number(segment)];
        else cursor = asRecord(cursor)?.[segment];
      }
      out[key] = spec.type === 'array' || spec.type === 'object' ? '' : scalarText(cursor);
    }
  }
  return out;
}

/** The `customFields` document a create or update sends, built from the flat values — empty inputs left out. */
export function partnerCustomFieldsOf(groups: readonly PartnerFieldGroup[], values: PartnerFieldValues): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const group of groups) {
    const groupOut: Record<string, unknown> = {};
    for (const { key, spec } of partnerFieldInputs(group)) {
      const raw = values[key] ?? '';
      if (raw.trim() === '') continue;
      const value: unknown = spec.type === 'number' ? Number(raw) : spec.type === 'boolean' ? raw === 'true' : raw;
      const path = key.split('.').slice(1);
      let cursor: Record<string, unknown> = groupOut;
      for (let i = 0; i < path.length - 1; i += 1) {
        const segment = path[i] ?? '';
        const nextIsIndex = /^\d+$/.test(path[i + 1] ?? '');
        if (nextIsIndex) {
          const list = Array.isArray(cursor[segment]) ? (cursor[segment] as Record<string, unknown>[]) : [];
          cursor[segment] = list;
          const index = Number(path[i + 1]);
          list[index] = list[index] ?? {};
          cursor = list[index];
          i += 1;
        } else {
          const next = asRecord(cursor[segment]) ?? {};
          cursor[segment] = next;
          cursor = next;
        }
      }
      cursor[path[path.length - 1] ?? ''] = value;
    }
    if (Object.keys(groupOut).length > 0) out[group.name] = groupOut;
  }
  return out;
}

/** The required inputs left empty — the console's own check before the service's (`isRequired`). */
export function missingRequiredPartnerFields(groups: readonly PartnerFieldGroup[], values: PartnerFieldValues): string[] {
  const missing: string[] = [];
  for (const group of groups) {
    for (const { key, spec } of partnerFieldInputs(group)) {
      if (spec.isRequired && (values[key] ?? '').trim() === '') missing.push(key);
    }
  }
  return missing;
}

/**
 * Every stored field as data, for a read-only view: the type's groups in order, THEN whatever the
 * document carries that the type does not name (an older type, another writer) — rendered, never
 * dropped (PS-8). A value that is not a scalar is shown as JSON.
 */
export function partnerFieldRows(groups: readonly PartnerFieldGroup[], customFields: Record<string, unknown> | undefined): readonly { readonly group: string; readonly groupLabel: string; readonly key: string; readonly label: string; readonly value: string; readonly known: boolean }[] {
  const rows: { group: string; groupLabel: string; key: string; label: string; value: string; known: boolean }[] = [];
  const seen = new Set<string>();
  const values = partnerFieldValuesOf(groups, customFields);
  for (const group of groups) {
    for (const { key, label } of partnerFieldInputs(group)) {
      seen.add(key);
      rows.push({ group: group.name, groupLabel: group.label, key, label, value: values[key] ?? '', known: true });
    }
  }
  const walk = (prefix: string, groupName: string, groupLabel: string, value: unknown): void => {
    const record = asRecord(value);
    if (record !== undefined) {
      for (const [name, child] of Object.entries(record)) walk(`${prefix}.${name}`, groupName, groupLabel, child);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((child, index) => {
        walk(`${prefix}.${String(index)}`, groupName, groupLabel, child);
      });
      return;
    }
    if (seen.has(prefix)) return;
    rows.push({ group: groupName, groupLabel, key: prefix, label: prefix.slice(groupName.length + 1), value: scalarText(value), known: false });
  };
  for (const [groupName, groupValue] of Object.entries(customFields ?? {})) {
    const known = groups.find((group) => group.name === groupName);
    walk(groupName, groupName, known?.label ?? groupName, groupValue);
  }
  return rows;
}

/**
 * The service's refusal of a document, mapped onto the form: `"BasicInformation.lastName" is
 * required` (the Joi sentence observed for a person without a last name) → the input's key; any
 * other sentence stays general. The message is shown as the service wrote it.
 */
export function partnerFieldRefusalOf(error: unknown): { readonly fields: Readonly<Record<string, string>>; readonly general: readonly string[] } {
  if (!isApiError(error) || error.status !== 400) return { fields: {}, general: [] };
  const body = asRecord(error.body);
  const raw: unknown = body?.message;
  const messages = Array.isArray(raw) ? raw.map(String) : typeof raw === 'string' ? [raw] : [];
  const fields: Record<string, string> = {};
  const general: string[] = [];
  for (const message of messages) {
    const key = /"([A-Za-z0-9_]+(?:\.[A-Za-z0-9_]+)+)"/.exec(message)?.[1];
    if (key !== undefined && fields[key] === undefined) fields[key] = message;
    else general.push(message);
  }
  return { fields, general };
}
